import { isoDateTimeSchema, uuidSchema, notebookModeSchema, dateOnlySchema, authorKindSchema } from './chunk-EWGKAU72.js';
import { z } from 'zod';

var FORMAT_VERSION = 1;
var ACCOUNT_ROOTS = {
  meta: "meta",
  spaces: "spaces",
  notebooks: "notebooks",
  settings: "settings",
  starred: "starred",
  tabs: "tabs",
  shared: "shared",
  trust: "trust",
  /** Shared spaces of others in this account's space switcher (D-62, §2.1). */
  sharedSpaces: "sharedSpaces"
};
var SPACE_ROOTS = {
  meta: "meta",
  notebooks: "notebooks"
};
var NOTEBOOK_ROOTS = {
  meta: "meta",
  folders: "folders",
  notes: "notes",
  tags: "tags"
};
var NOTE_ROOTS = {
  content: "content",
  meta: "meta",
  strokes: "strokes",
  comments: "comments"
};
var spaceSchema = z.object({
  id: uuidSchema,
  name: z.string(),
  /** Emoji or SF Symbol name. */
  icon: z.string().nullable(),
  /** Color token. */
  color: z.string().nullable(),
  order: z.number(),
  createdAt: isoDateTimeSchema,
  /** The space is shared (D-62): the space document is the source of name and icon. */
  shared: z.boolean().optional()
});
var notebookRefSchema = z.object({
  id: uuidSchema,
  spaceId: uuidSchema,
  name: z.string(),
  icon: z.string().nullable(),
  color: z.string().nullable(),
  order: z.number(),
  mode: notebookModeSchema,
  createdAt: isoDateTimeSchema
});
var STARRED_KINDS = ["note", "folder", "notebook"];
var starredItemSchema = z.object({ kind: z.enum(STARRED_KINDS), id: uuidSchema });
var tabItemSchema = z.object({ noteId: uuidSchema });
var ACCOUNT_SETTINGS_KEYS = {
  /** Notebook that holds daily notes and the inbox (§3). */
  dailyNotebookId: "dailyNotebookId",
  /** noteId of the last active tab (§2). */
  activeTab: "activeTab",
  /** The user confirmed they saved the current recovery key (reset on rotation). */
  recoveryKeyConfirmed: "recoveryKeyConfirmed"
};
var folderSchema = z.object({
  id: uuidSchema,
  parentId: uuidSchema.nullable(),
  name: z.string(),
  icon: z.string().nullable(),
  color: z.string().nullable(),
  order: z.number(),
  createdAt: isoDateTimeSchema
});
var NOTE_KINDS = ["note", "daily", "inbox"];
var noteMetaSchema = z.object({
  id: uuidSchema,
  folderId: uuidSchema.nullable(),
  parentNoteId: uuidSchema.nullable(),
  title: z.string(),
  kind: z.enum(NOTE_KINDS),
  dailyDate: dateOnlySchema.nullable(),
  tags: z.array(z.string()),
  icon: z.string().nullable(),
  /** attachmentId of the cover thumbnail. */
  coverThumb: z.string().nullable(),
  /** noteKey wrapped by the notebookKey, base64url envelope (crypto.md). */
  wrappedKey: z.string(),
  createdAt: isoDateTimeSchema,
  updatedAt: isoDateTimeSchema,
  trashedAt: isoDateTimeSchema.nullable(),
  order: z.number(),
  /**
   * The whole note was created by an agent (D-45): clients show a "Created by
   * agent" strip; "Got it" sets null. Optional — older documents lack it.
   */
  createdByAgent: z.object({ agentId: z.string(), agentName: z.string(), at: isoDateTimeSchema }).nullable().optional(),
  /** Version of the noteKey (D-54, crypto.md §10.3); absent = 1. */
  keyVersion: z.number().int().positive().optional(),
  /** Wrapped previous noteKeys, newest first (crypto.md §10.3); absent = []. */
  keyChain: z.array(z.string()).optional()
});
var sharedRefSchema = z.object({
  kind: z.enum(["notebook", "note"]),
  id: uuidSchema,
  spaceId: uuidSchema,
  order: z.number(),
  addedAt: isoDateTimeSchema
});
var sharedRefKey = (kind, id) => `${kind}:${id}`;
var sharedSpaceRefSchema = z.object({
  order: z.number(),
  addedAt: isoDateTimeSchema
});
var spaceMetaSchema = z.object({
  formatVersion: z.literal(FORMAT_VERSION),
  spaceId: uuidSchema,
  name: z.string(),
  icon: z.string().nullable(),
  color: z.string().nullable()
});
var spaceNotebookRefSchema = notebookRefSchema.omit({ spaceId: true });
var notebookMetaSchema = z.object({
  formatVersion: z.number().int().positive(),
  notebookId: uuidSchema,
  name: z.string(),
  icon: z.string().nullable(),
  space: z.object({ id: uuidSchema, name: z.string(), icon: z.string().nullable() }).nullable()
});
var tagPropsSchema = z.object({
  color: z.string().nullable(),
  icon: z.string().nullable()
});
var TRASH_RETENTION_DAYS = 30;
var NOTE_FONTS = ["serif", "sans", "mono"];
var NOTE_WIDTHS = ["normal", "wide", "full"];
var NOTE_PAPERS = ["plain", "dots", "grid", "lines"];
var noteStyleSchema = z.object({
  font: z.enum(NOTE_FONTS),
  width: z.enum(NOTE_WIDTHS),
  paper: z.enum(NOTE_PAPERS),
  textColor: z.string().nullable()
});
var DEFAULT_NOTE_STYLE = Object.freeze({
  font: "sans",
  width: "normal",
  paper: "plain",
  textColor: null
});
var noteCoverSchema = z.object({
  attachmentId: uuidSchema,
  offsetY: z.number().min(0).max(1)
});
var noteBackdropSchema = z.object({
  kind: z.enum(["color", "gradient"]),
  value: z.string()
});
var commentAuthorSchema = z.object({
  kind: authorKindSchema,
  id: z.string(),
  name: z.string()
});
var commentSchema = z.object({
  id: uuidSchema,
  blockId: uuidSchema,
  /** Relative positions (Y.encodeRelativePosition) in base64url, or null for the whole block. */
  anchor: z.object({ start: z.string(), end: z.string() }).nullable(),
  author: commentAuthorSchema,
  text: z.string(),
  createdAt: isoDateTimeSchema,
  resolvedAt: isoDateTimeSchema.nullable()
});
var BLOCK_NODES = [
  "title",
  "paragraph",
  "heading",
  "bulletList",
  "orderedList",
  "listItem",
  "taskList",
  "taskItem",
  "blockquote",
  "callout",
  "codeBlock",
  "mathBlock",
  "mermaid",
  "horizontalRule",
  "table",
  "tableRow",
  "tableHeader",
  "tableCell",
  "image",
  "file",
  "drawing",
  "pageCard",
  "collection"
];
var INLINE_NODES = ["noteLink", "tag", "mention", "hardBreak"];
var MARKS = [
  "bold",
  "italic",
  "underline",
  "strike",
  "code",
  "link",
  "highlight",
  "textColor",
  "agentEdit"
];
var TEXTBLOCK_NODES = ["title", "paragraph", "heading", "codeBlock"];
var ATOM_BLOCK_NODES = [
  "mathBlock",
  "mermaid",
  "horizontalRule",
  "image",
  "file",
  "drawing",
  "pageCard"
];
var TASK_STATES = ["open", "done", "cancelled"];
var CALLOUT_TONES = ["note", "info", "success", "warning", "danger"];
var HR_STYLES = ["line", "dots", "wave"];
var PAGE_CARD_STYLES = ["card", "link"];
var pmMarkSchema = z.object({
  type: z.string().min(1),
  attrs: z.record(z.string(), z.unknown()).optional()
});
var pmNodeSchema = z.lazy(
  () => z.object({
    type: z.string().min(1),
    attrs: z.record(z.string(), z.unknown()).optional(),
    content: z.array(pmNodeSchema).optional(),
    text: z.string().optional(),
    marks: z.array(pmMarkSchema).optional()
  })
);

export { ACCOUNT_ROOTS, ACCOUNT_SETTINGS_KEYS, ATOM_BLOCK_NODES, BLOCK_NODES, CALLOUT_TONES, DEFAULT_NOTE_STYLE, FORMAT_VERSION, HR_STYLES, INLINE_NODES, MARKS, NOTEBOOK_ROOTS, NOTE_FONTS, NOTE_KINDS, NOTE_PAPERS, NOTE_ROOTS, NOTE_WIDTHS, PAGE_CARD_STYLES, SPACE_ROOTS, STARRED_KINDS, TASK_STATES, TEXTBLOCK_NODES, TRASH_RETENTION_DAYS, commentAuthorSchema, commentSchema, folderSchema, noteBackdropSchema, noteCoverSchema, noteMetaSchema, noteStyleSchema, notebookMetaSchema, notebookRefSchema, pmNodeSchema, sharedRefKey, sharedRefSchema, sharedSpaceRefSchema, spaceMetaSchema, spaceNotebookRefSchema, spaceSchema, starredItemSchema, tabItemSchema, tagPropsSchema };
//# sourceMappingURL=chunk-PP2CXEGZ.js.map
//# sourceMappingURL=chunk-PP2CXEGZ.js.map